import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TenantsService } from '../../services/tenants.service';
import { SelectedTenantService } from '../../services/selected-tenant.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantSummary } from '../../models/tenant.model';
import { LoaderComponent } from '../../common/loader/loader.component';

@Component({
  selector: 'app-tenant-base',
  imports: [LoaderComponent],
  templateUrl: './tenant-base.component.html',
  styleUrl: './tenant-base.component.scss'
})
export class TenantBaseComponent implements OnInit {
  tenant: TenantSummary | null = null;
  cards: { key: string; icon: string; title: string; description: string; footer: string }[] = [];
  loading = true;

  private tenantUid: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tenantsService: TenantsService,
    private selectedTenantService: SelectedTenantService,
    private toastrService: ToastrService,
    private titleService: Title
  ) {
    this.tenantUid = this.route.snapshot.paramMap.get('slug') ?? '';
  }

  ngOnInit(): void {
    const cached = this.selectedTenantService.tenant();

    if (cached && cached.tenant_uid === this.tenantUid) {
      this.applyTenant(cached);
      return;
    }

    // Direct URL load / refresh: no tenant in memory yet, fall back to fetching it.
    this.tenantsService.getTenants().subscribe({
      next: (tenants) => {
        const found = tenants.find(t => t.tenant_uid === this.tenantUid) ?? null;
        this.loading = false;

        if (found) {
          this.selectedTenantService.set(found);
          this.applyTenant(found);
        } else {
          this.toastrService.error('Tenant not found.');
          this.router.navigate(['/tenants']);
        }
      },
      error: () => {
        this.loading = false;
        this.toastrService.error('Unable to load tenant details.');
        this.router.navigate(['/tenants']);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/tenants']);
  }

  onCardClick(cardKey: string): void {
    if (!this.tenant) {
      return;
    }
    if (cardKey === 'services' || cardKey === 'sso' || cardKey === 'users' || cardKey === 'roles') {
      this.router.navigate(['/tenants', this.tenant.tenant_uid, cardKey]);
    }
  }

  private applyTenant(tenant: TenantSummary): void {
    this.tenant = tenant;
    this.loading = false;
    this.cards = this.buildCards(tenant);
    this.titleService.setTitle(`${tenant.name} · Admin Panel`);
  }

  private buildCards(tenant: TenantSummary) {
    return [
      { key: 'services', icon: 'bi-hdd-network', title: 'Services', description: 'Manage services and database credentials', footer: `${tenant.assigned_services_count} Services Configured` },
      { key: 'sso', icon: 'bi-shield-lock', title: 'Security (SSO)', description: 'Manage SSO and authentication settings', footer: 'SSO Configuration' },
      { key: 'users', icon: 'bi-people', title: 'Users', description: 'Manage users and their access', footer: `${tenant.active_users_count} Active Users` },
      { key: 'roles', icon: 'bi-shield-lock', title: 'Roles', description: 'Manage roles and permissions', footer: 'Roles & Permissions' }
    ];
  }
}
